import { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "../../../App/store"
import { updateUserTutorialProgressRequest } from "../../User/UserProfile/UserTutorialProgressRequest"
import { setUserData } from "../SharedSlices/UserDataSlice"
import StorageService from "../../../App/StorageService"
import { useNavigate } from "react-router"
import { RoleType } from "../../../App/enums"
import { PatchOrganizationTutorialProgressRequest } from "../../Organization/OrganizationSettings/OrganizationAccountServiceRequest"
import { setOrganizationData } from "../SharedSlices/OrganizationsSlice"
import { getTutorialStepDefinition, getTutorialTargetElement, TutorialPlacement } from "../Onboarding/TutorialFlow"
import { selectTranslations } from "../../../App/i18n/i18nSlice"

type TutorialBubbleProps = {
    text: string
    visible: boolean
    hasNext: boolean
    routeOnNext?: string
    targetSelector?: string
    placement?: TutorialPlacement
}

type PointedPlacement = Exclude<TutorialPlacement, "auto">

const placementOrder: PointedPlacement[] = ["bottom", "right", "left", "top"]
const tutorialBackdropZIndex = 1000012
const tutorialSpotlightZIndex = 1000013
const tutorialBubbleZIndex = 1000014

const getPlacementCandidates = (preferred: TutorialPlacement): PointedPlacement[] => {
    if (preferred !== "auto") {
        return [preferred, ...placementOrder.filter(item => item !== preferred)]
    }

    return placementOrder
}

const getCoordinateForPlacement = (
    placement: PointedPlacement,
    targetRect: DOMRect,
    bubbleRect: DOMRect,
    gap: number
) => {
    if (placement === "top") {
        return {
            top: targetRect.top - bubbleRect.height - gap,
            left: targetRect.left + targetRect.width / 2 - bubbleRect.width / 2,
        }
    }

    if (placement === "left") {
        return {
            top: targetRect.top + targetRect.height / 2 - bubbleRect.height / 2,
            left: targetRect.left - bubbleRect.width - gap,
        }
    }

    if (placement === "right") {
        return {
            top: targetRect.top + targetRect.height / 2 - bubbleRect.height / 2,
            left: targetRect.right + gap,
        }
    }

    return {
        top: targetRect.bottom + gap,
        left: targetRect.left + targetRect.width / 2 - bubbleRect.width / 2,
    }
}

const getOverflowScore = (
    top: number,
    left: number,
    bubbleRect: DOMRect,
    viewportPadding: number
) => {
    const overflowLeft = Math.max(0, viewportPadding - left)
    const overflowTop = Math.max(0, viewportPadding - top)
    const overflowRight = Math.max(0, left + bubbleRect.width + viewportPadding - window.innerWidth)
    const overflowBottom = Math.max(0, top + bubbleRect.height + viewportPadding - window.innerHeight)

    return overflowLeft + overflowTop + overflowRight + overflowBottom
}

const isTargetComfortablyVisible = (targetRect: DOMRect) => {
    const verticalSafeArea = Math.min(140, Math.max(80, window.innerHeight * 0.14))
    const horizontalSafeArea = 20

    const topVisible = targetRect.top >= verticalSafeArea
    const bottomVisible = targetRect.bottom <= window.innerHeight - verticalSafeArea
    const leftVisible = targetRect.left >= horizontalSafeArea
    const rightVisible = targetRect.right <= window.innerWidth - horizontalSafeArea

    return topVisible && bottomVisible && leftVisible && rightVisible
}

const closeMobileBurgerMenuIfOpen = () => {
    if (typeof window === "undefined" || window.innerWidth > 1023) {
        return
    }

    const burgerMenu = document.querySelector(".header-burger-menu")
    const burgerToggle = document.querySelector('[data-tutorial-anchor="header-burger-toggle"]') as HTMLElement | null

    if (burgerMenu && burgerToggle) {
        burgerToggle.click()
    }
}

const closeRoomMobileMenuIfOpen = () => {
    if (typeof window === "undefined" || window.innerWidth > 1023) {
        return
    }

    const roomMobileMenu = document.querySelector(".room-mobile-menu")
    const roomMobileMenuToggle = document.querySelector('[data-tutorial-anchor="room-mobile-menu-toggle"]') as HTMLElement | null

    if (roomMobileMenu && roomMobileMenuToggle) {
        roomMobileMenuToggle.click()
    }
}

function TutorialBubble(props: TutorialBubbleProps) {
    const dispatch = useDispatch()
    const translation = useSelector(selectTranslations)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const orgDataState = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    const [isVisible, setIsVisible] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)
    const [actualPlacement, setActualPlacement] = useState<PointedPlacement>("bottom")
    const [positionStyle, setPositionStyle] = useState<CSSProperties>()
    const [highlightStyle, setHighlightStyle] = useState<CSSProperties>()
    const bubbleRef = useRef<HTMLDivElement>(null)
    const hasAutoScrolledRef = useRef(false)
    const hasRevealAttemptRef = useRef(false)
    const navigate = useNavigate()
    const maxStep = role === RoleType.ORGANIZATION ? 10 : 7
    const currentStep = role === RoleType.ORGANIZATION ? orgDataState.tutorial_progress : userDataState.tutorial_progress
    const stepDefinition = getTutorialStepDefinition(role, currentStep)
    const targetSelector = props.targetSelector || stepDefinition?.targetSelectors || stepDefinition?.targetSelector
    const placement = stepDefinition?.preferredPlacement || props.placement || "auto"
    const gap = stepDefinition?.gap || 20
    const offsetX = stepDefinition?.offset?.x || 0
    const offsetY = stepDefinition?.offset?.y || 0
    const spotlightPadding = stepDefinition?.spotlightPadding || 8

    useEffect(() => {
        hasAutoScrolledRef.current = false
        hasRevealAttemptRef.current = false
    }, [targetSelector, isVisible])

    useEffect(() => {
        setIsVisible(props.visible)
    }, [props.visible])

    useEffect(() => {
        if(Object.keys(userDataState).length > 0){
            StorageService.setItem("user", StorageService.stringify(userDataState))
        }
    }, [userDataState])

    const updatePosition = useCallback(() => {
        if (!isVisible || !bubbleRef.current) {
            return
        }

        const bubbleElement = bubbleRef.current
        const targetElement = getTutorialTargetElement(targetSelector)

        if (!targetElement) {
            const revealSelectors = stepDefinition?.revealTargetSelectors?.length
                ? stepDefinition.revealTargetSelectors
                : stepDefinition?.revealTargetSelector
                    ? [stepDefinition.revealTargetSelector]
                    : []

            if (
                !hasRevealAttemptRef.current &&
                revealSelectors.length > 0 &&
                typeof window !== "undefined" &&
                window.innerWidth <= 1023
            ) {
                hasRevealAttemptRef.current = true

                revealSelectors.forEach((selector, index) => {
                    window.setTimeout(() => {
                        const revealTrigger = document.querySelector(selector) as HTMLElement | null
                        revealTrigger?.click()
                    }, index * 120)
                })

                window.setTimeout(() => {
                    updatePosition()
                }, revealSelectors.length * 140 + 120)

                return
            }

            setActualPlacement("bottom")
            setHighlightStyle(undefined)
            setPositionStyle({
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: tutorialBubbleZIndex,
                visibility: "visible",
            })
            return
        }

        const targetRect = targetElement.getBoundingClientRect()
        const spotLeft = Math.max(targetRect.left - spotlightPadding, 8)
        const spotTop = Math.max(targetRect.top - spotlightPadding, 8)
        const spotRight = Math.min(targetRect.right + spotlightPadding, window.innerWidth - 8)
        const spotBottom = Math.min(targetRect.bottom + spotlightPadding, window.innerHeight - 8)

        setHighlightStyle({
            position: "fixed",
            top: `${spotTop}px`,
            left: `${spotLeft}px`,
            width: `${Math.max(20, spotRight - spotLeft)}px`,
            height: `${Math.max(20, spotBottom - spotTop)}px`,
            zIndex: tutorialSpotlightZIndex,
        })

        const targetOutsideViewport =
            targetRect.bottom < 0 ||
            targetRect.top > window.innerHeight ||
            targetRect.right < 0 ||
            targetRect.left > window.innerWidth

        const targetNeedsScroll = targetOutsideViewport || !isTargetComfortablyVisible(targetRect)

        if (targetNeedsScroll && !hasAutoScrolledRef.current) {
            hasAutoScrolledRef.current = true
            targetElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" })
        }

        const bubbleRect = bubbleElement.getBoundingClientRect()
        const viewportPadding = 16
        const candidates = getPlacementCandidates(placement)

        const bestPlacement = candidates
            .map(candidate => {
                const coordinates = getCoordinateForPlacement(candidate, targetRect, bubbleRect, gap)
                const overflowScore = getOverflowScore(coordinates.top, coordinates.left, bubbleRect, viewportPadding)

                return {
                    placement: candidate,
                    top: coordinates.top,
                    left: coordinates.left,
                    overflowScore,
                }
            })
            .sort((left, right) => left.overflowScore - right.overflowScore)[0]

        let nextTop = bestPlacement.top + offsetY
        let nextLeft = bestPlacement.left + offsetX

        nextLeft = Math.min(Math.max(nextLeft, viewportPadding), window.innerWidth - bubbleRect.width - viewportPadding)
        nextTop = Math.min(Math.max(nextTop, viewportPadding), window.innerHeight - bubbleRect.height - viewportPadding)

        setActualPlacement(bestPlacement.placement)

        setPositionStyle({
            position: "fixed",
            top: `${nextTop}px`,
            left: `${nextLeft}px`,
            zIndex: tutorialBubbleZIndex,
            visibility: "visible",
        } as CSSProperties)
    }, [
        gap,
        isVisible,
        offsetX,
        offsetY,
        placement,
        spotlightPadding,
        stepDefinition?.revealTargetSelector,
        stepDefinition?.revealTargetSelectors,
        targetSelector,
    ])

    useLayoutEffect(() => {
        if (!isVisible) {
            return
        }

        const frame = window.requestAnimationFrame(updatePosition)
        const targetElement = getTutorialTargetElement(targetSelector)
        const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => updatePosition()) : null
        const mutationObserver = typeof MutationObserver !== "undefined" ? new MutationObserver(() => updatePosition()) : null

        if (targetElement && resizeObserver) {
            resizeObserver.observe(targetElement)
        }

        if (bubbleRef.current && resizeObserver) {
            resizeObserver.observe(bubbleRef.current)
        }

        if (mutationObserver) {
            mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true })
        }

        const onViewportChange = () => updatePosition()

        window.addEventListener("resize", onViewportChange)
        window.addEventListener("scroll", onViewportChange, true)

        return () => {
            window.cancelAnimationFrame(frame)
            resizeObserver?.disconnect()
            mutationObserver?.disconnect()
            window.removeEventListener("resize", onViewportChange)
            window.removeEventListener("scroll", onViewportChange, true)
        }
    }, [isVisible, targetSelector, updatePosition])

    const handleOnNext = async () => {
        if (isUpdating) {
            return
        }

        setIsUpdating(true)

        switch (role){
            case RoleType.USER:
                await updateUserTutorialProgressRequest(userDataState, { tutorial_progress: userDataState.tutorial_progress! + 1 }).then(() => {
                    dispatch(setUserData({ ...userDataState, tutorial_progress: userDataState.tutorial_progress! + 1 }))
        
                    if(props.routeOnNext){
                        navigate(props.routeOnNext)
                    }

                    closeMobileBurgerMenuIfOpen()
                    closeRoomMobileMenuIfOpen()
                    setIsVisible(false)
                })
            break
            case RoleType.ORGANIZATION:
                await PatchOrganizationTutorialProgressRequest(orgDataState.id!,orgDataState.tutorial_progress! + 1).then(() => {
                    dispatch(setOrganizationData({...orgDataState,tutorial_progress: orgDataState.tutorial_progress! +1}))

                    if(props.routeOnNext){
                        
                        navigate(props.routeOnNext)
                    }

                    closeMobileBurgerMenuIfOpen()
                    closeRoomMobileMenuIfOpen()

                    setIsVisible(false)
                })
            break
        }

        setIsUpdating(false)
    }

    const handleOnPrevious = async () => {
        if (isUpdating) {
            return
        }

        if (!currentStep || currentStep <= 1) {
            return
        }

        const previousStep = currentStep - 1
        const previousStepDefinition = getTutorialStepDefinition(role, previousStep)
        const previousStepRoute = previousStepDefinition?.fallbackRoute?.({ organizationId: orgDataState.id })
        const previousStepTarget = getTutorialTargetElement(previousStepDefinition?.targetSelectors || previousStepDefinition?.targetSelector)

        setIsUpdating(true)

        switch (role) {
            case RoleType.USER:
                await updateUserTutorialProgressRequest(userDataState, { tutorial_progress: previousStep }).then(() => {
                    dispatch(setUserData({ ...userDataState, tutorial_progress: previousStep }))

                    if (previousStepRoute) {
                        navigate(previousStepRoute)
                    } else if (!previousStepTarget && window.history.length > 1) {
                        navigate(-1)
                    }

                    closeMobileBurgerMenuIfOpen()
                    closeRoomMobileMenuIfOpen()
                    setIsVisible(false)
                })
                break
            case RoleType.ORGANIZATION:
                await PatchOrganizationTutorialProgressRequest(orgDataState.id!, previousStep).then(() => {
                    dispatch(setOrganizationData({ ...orgDataState, tutorial_progress: previousStep }))

                    if (previousStepRoute) {
                        navigate(previousStepRoute)
                    } else if (!previousStepTarget && window.history.length > 1) {
                        navigate(-1)
                    }

                    closeMobileBurgerMenuIfOpen()
                    closeRoomMobileMenuIfOpen()
                    setIsVisible(false)
                })
                break
        }

        setIsUpdating(false)
    }

    const handleOnExit = async () => {
        if (isUpdating) {
            return
        }

        setIsUpdating(true)

        switch (role){
            case RoleType.USER:
                await updateUserTutorialProgressRequest(userDataState, { tutorial_progress: 100 }).then(() => {
                    dispatch(setUserData({ ...userDataState, tutorial_progress: 100 }))
                })
                closeMobileBurgerMenuIfOpen()
                closeRoomMobileMenuIfOpen()
                setIsVisible(false)
            break
            case RoleType.ORGANIZATION:
                await PatchOrganizationTutorialProgressRequest(orgDataState.id!,100).then(() => {
                    dispatch(setOrganizationData({...orgDataState,tutorial_progress: 100}))
                })
                closeMobileBurgerMenuIfOpen()
                closeRoomMobileMenuIfOpen()
                setIsVisible(false)
            break
        }

        setIsUpdating(false)
    }

    useEffect(() => {
        if (!isVisible) {
            return
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault()
                handleOnExit()
            }

            if (event.key === "Enter" && props.hasNext) {
                event.preventDefault()
                handleOnNext()
            }
        }

        document.addEventListener("keydown", onKeyDown)

        return () => {
            document.removeEventListener("keydown", onKeyDown)
        }
    }, [handleOnExit, handleOnNext, isVisible, props.hasNext])

    if (!isVisible || typeof document === "undefined") {
        return null
    }

    return createPortal(
        <>
            <div className="speechbubble__backdrop" style={{ zIndex: tutorialBackdropZIndex }} />
            {highlightStyle && <div className="speechbubble__spotlight" style={highlightStyle} />}
            <div ref={bubbleRef} className={`speechbubble speechbubble--${actualPlacement}`} style={positionStyle}>
                <div className="speechbubble__title-row">
                    <span className="speechbubble__title">Onboarding</span>
                    <span className="speechbubble__step">Schritt {currentStep || 1}/{maxStep}</span>
                </div>
                <div className="speechbubble__row">
                    {props.text}
                </div>
                <div className="speechbubble__bottom-panel">
                    <button className="speechbubble__button speechbubble__button--ghost" onClick={handleOnExit} disabled={isUpdating}>
                        Beenden
                    </button>
                    {(currentStep || 1) > 1 && (
                        <button className="speechbubble__button speechbubble__button--ghost" onClick={handleOnPrevious} disabled={isUpdating}>
                            {translation.previous ?? "Zurück"}
                        </button>
                    )}
                    {props.hasNext && (
                        <button className="speechbubble__button speechbubble__button--primary" onClick={handleOnNext} disabled={isUpdating}>
                            Weiter
                        </button>
                    )}
                </div>
            </div>
        </>,
        document.body
    )
}
export default TutorialBubble
